import { BFF_URL } from "@/lib/const";

type EndUserTool = {
  /** The full, version-less tool ref, e.g. `"Github.CreateIssue"`. */
  ref: string;
  /** The tool name with its toolkit prefix removed, e.g. `"CreateIssue"`. */
  name: string;
};

export type ToolkitGroup = {
  /** The toolkit prefix (segment before the first `.`), e.g. `"Github"`. */
  toolkit: string;
  tools: EndUserTool[];
};

/**
 * Group bare tool refs by their toolkit prefix (the segment before the first
 * `.`). Toolkits and the tools within each toolkit are sorted alphabetically so
 * the rendered list is stable regardless of the order the backend returns.
 * A ref with no `.` becomes its own single-tool group.
 */
export const groupRefsByToolkit = (refs: string[]): ToolkitGroup[] => {
  const byToolkit = new Map<string, EndUserTool[]>();

  for (const ref of refs) {
    const dotIndex = ref.indexOf(".");
    const toolkit = dotIndex === -1 ? ref : ref.slice(0, dotIndex);
    const name = dotIndex === -1 ? ref : ref.slice(dotIndex + 1);

    const tools = byToolkit.get(toolkit) ?? [];
    if (!tools.some((tool) => tool.ref === ref)) {
      tools.push({ ref, name });
    }
    byToolkit.set(toolkit, tools);
  }

  return Array.from(byToolkit, ([toolkit, tools]) => ({
    toolkit,
    tools: tools.sort((a, b) => a.name.localeCompare(b.name)),
  })).sort((a, b) => a.toolkit.localeCompare(b.toolkit));
};

/**
 * Build the absolute BFF login URL that starts the end-user IdP flow. The `/eu`
 * route guard redirects the browser here when there is no end-user session.
 * `URLSearchParams` handles encoding, so `returnTo` (a full `/eu` URL) is safe
 * to pass through verbatim.
 */
export const buildEnduserLoginUrl = ({
  projectId,
  userSourceId,
  returnTo,
}: {
  projectId: string;
  userSourceId: string;
  returnTo: string;
}): string => {
  const params = new URLSearchParams({
    project_id: projectId,
    user_source_id: userSourceId,
    return_to: returnTo,
  });
  return `${BFF_URL}/api/enduser/login?${params.toString()}`;
};
